import type { WebApp } from "./webAnalytics"

// The one Sentry setup every Agilix web app shares (Adam, 2026-11-03): one project, `web`, every event tagged with
// its `app`, the release named `<app>@<short sha>` so each app's source maps resolve. Plain option objects with no
// @sentry import - the packages pin different @sentry/astro versions and pass these to their own `Sentry.init`.
// A DSN is public by design, so it lives here as a constant.

export const SENTRY_ORG = "hackerman-consulting"
export const SENTRY_WEB_PROJECT = "web"
export const SENTRY_WEB_DSN = "https://24166222533259a5beb05bf321ffe1ab@o254158.ingest.us.sentry.io/4512369118281728"

// The release the build uploads source maps under and the Vite plugin stamps into the bundle. `RELEASE_VERSION`
// is the short sha deploy.yaml passes as a build arg; a local build has none.
export const LOCAL_RELEASE_VERSION = "local"

export const sentryReleaseName = (app: WebApp, releaseVersion: string | null): string =>
  `${app}@${releaseVersion ?? LOCAL_RELEASE_VERSION}`

// Errors no page of ours causes or can act on. Every app's list merged; reasons per entry where not obvious.
export const SENTRY_WEB_IGNORE_ERRORS: Array<string | RegExp> = [
  "Importing a module script failed",
  "error loading dynamically imported module",
  /webkit\.messageHandlers/,
  "auth/network-request-failed",
  "Root did not complete",
  "Failed to fetch dynamically imported module",
  "'text/html' is not a valid JavaScript MIME type",
  "Load failed",
  "Network Error",
  "NetworkError when attempting to fetch resource",
  "Fetch is aborted",
  "AbortError",
  "Connection to Indexed Database",
  "Database deleted by request",
  "NotAllowedError",
  "Multiple Sentry Session Replay",
  // Instagram's and Facebook's in-app browsers post their own telemetry to their native layer on `beforeunload`
  // ("Java object is gone" / "Java exception was raised during method invocation"); Sentry blames us only
  // because it wraps `addEventListener`.
  /Error invoking postMessage/,
  // Scripts Brave on iOS and Facebook's in-app browser inject, at line 1 of the document (seen 2026-10-30).
  "window.__firefox__.reader.checkReadability",
  "window._META.IABInnerFrame",
  // Chrome skipping the cross-document view transition on a hidden tab or a mid-navigation resize; the page
  // still loads (seen 2026-11-02).
  "Transition was aborted because of invalid state",
  "Skipping view transition because viewport size changed",
]

export const webSentryClientOptions = (app: WebApp) => ({
  dsn: SENTRY_WEB_DSN,
  tracesSampleRate: 0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  ignoreErrors: SENTRY_WEB_IGNORE_ERRORS,
  initialScope: {
    tags: {
      app,
    },
  },
})

// The SSR half. Without a server init file @sentry/astro builds its own with `tracesSampleRate: 1` and no tags,
// so every page was traced (home hit `429 span_usage_exceeded` org-wide) and SSR errors landed untagged.
export const webSentryServerOptions = (app: WebApp) => ({
  dsn: SENTRY_WEB_DSN,
  tracesSampleRate: 0,
  initialScope: {
    tags: {
      app,
    },
  },
})

// The build-time half for `astro.config.ts`: upload to `web` under the per-app release.
export const webSentryBuildOptions = (app: WebApp, authToken: string, releaseVersion: string | null) => ({
  org: SENTRY_ORG,
  project: SENTRY_WEB_PROJECT,
  sourceMapsUploadOptions: {
    project: SENTRY_WEB_PROJECT,
    authToken,
  },
  unstable_sentryVitePluginOptions: {
    release: {
      name: sentryReleaseName(app, releaseVersion),
    },
  },
})

// For the same-origin tunnel routes (home, zawody): the envelope URL of `web`, and whether an envelope names it.
const webDsnUrl = new URL(SENTRY_WEB_DSN)
export const SENTRY_WEB_HOST = webDsnUrl.hostname
export const SENTRY_WEB_PROJECT_ID = webDsnUrl.pathname.replace("/", "")
export const SENTRY_WEB_ENVELOPE_URL = `https://${SENTRY_WEB_HOST}/api/${SENTRY_WEB_PROJECT_ID}/envelope/`
